import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

const ConsultorioCitasContext = createContext(null);

const defaultPanel = {
  show: false,
  view: 'list',
  listTab: 'pending',
  filterPacienteId: null,
  highlightCitaId: null,
  diaSeleccionado: null,
  lockPacienteId: null,
};

export function ConsultorioCitasProvider({ children }) {
  const [panel, setPanel] = useState(defaultPanel);
  const [showCitaModal, setShowCitaModal] = useState(false);
  const handlersRef = useRef({});

  const bindCitasHandlers = useCallback((handlers) => {
    handlersRef.current = handlers || {};
  }, []);

  const openCitasPanel = useCallback((opts = {}) => {
    setPanel((prev) => ({
      ...defaultPanel,
      ...prev,
      show: true,
      view: opts.view ?? 'list',
      listTab: opts.listTab ?? 'pending',
      filterPacienteId: opts.filterPacienteId ?? null,
      highlightCitaId: opts.highlightCitaId ?? null,
      diaSeleccionado: opts.diaSeleccionado ?? prev.diaSeleccionado,
      lockPacienteId: opts.lockPacienteId ?? opts.filterPacienteId ?? null,
    }));
  }, []);

  const closeCitasPanel = useCallback(() => {
    setPanel((prev) => ({ ...prev, show: false, highlightCitaId: null }));
  }, []);

  const setPanelView = useCallback((view) => {
    setPanel((prev) => ({ ...prev, view }));
  }, []);

  const setListTab = useCallback((listTab) => {
    setPanel((prev) => ({ ...prev, listTab, view: 'list' }));
  }, []);

  const setDiaSeleccionado = useCallback((dia) => {
    setPanel((prev) => ({ ...prev, diaSeleccionado: dia }));
  }, []);

  const setHighlightCitaId = useCallback((id) => {
    setPanel((prev) => ({ ...prev, highlightCitaId: id }));
  }, []);

  const openNuevaCita = useCallback((opts = {}) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    setPanel((prev) => ({
      ...prev,
      show: true,
      view: 'schedule',
      filterPacienteId: opts.pacienteId ?? prev.filterPacienteId,
      lockPacienteId: opts.pacienteId ?? prev.lockPacienteId,
      highlightCitaId: null,
      diaSeleccionado: opts.date ?? today,
    }));
  }, []);

  const openCitaEnPanel = useCallback((cita) => {
    if (!cita) return;
    const patch = {
      show: true,
      highlightCitaId: cita.id ?? null,
      filterPacienteId: cita.idPaciente ?? null,
    };
    if (cita.fechaHora) {
      const d = new Date(cita.fechaHora);
      d.setHours(0, 0, 0, 0);
      patch.diaSeleccionado = d;
      patch.view = 'schedule';
    } else {
      patch.view = 'list';
    }
    setPanel((prev) => ({ ...prev, ...patch }));
  }, []);

  const openEditarCita = useCallback((cita) => {
    handlersRef.current.openEditarCita?.(cita);
  }, []);

  const openNuevaCitaModal = useCallback((opts = {}) => {
    handlersRef.current.openNuevaCitaModal?.(opts);
  }, []);

  const applyCitaFormPatch = useCallback((patch = {}) => {
    handlersRef.current.applyCitaFormPatch?.(patch);
  }, []);

  const saveCitaForm = useCallback(() => {
    return handlersRef.current.saveCitaForm?.();
  }, []);

  const changeEstadoCita = useCallback((cita, estado) => {
    return handlersRef.current.changeEstadoCita?.(cita, estado);
  }, []);

  const value = useMemo(
    () => ({
      panel,
      showCitaModal,
      setShowCitaModal,
      openCitasPanel,
      closeCitasPanel,
      setPanelView,
      setListTab,
      setDiaSeleccionado,
      setHighlightCitaId,
      openNuevaCita,
      openNuevaCitaModal,
      applyCitaFormPatch,
      saveCitaForm,
      openCitaEnPanel,
      openEditarCita,
      changeEstadoCita,
      bindCitasHandlers,
    }),
    [
      panel,
      showCitaModal,
      openCitasPanel,
      closeCitasPanel,
      setPanelView,
      setListTab,
      setDiaSeleccionado,
      setHighlightCitaId,
      openNuevaCita,
      openNuevaCitaModal,
      applyCitaFormPatch,
      saveCitaForm,
      openCitaEnPanel,
      openEditarCita,
      changeEstadoCita,
      bindCitasHandlers,
    ],
  );

  return (
    <ConsultorioCitasContext.Provider value={value}>
      {children}
    </ConsultorioCitasContext.Provider>
  );
}

export function useConsultorioCitas() {
  const ctx = useContext(ConsultorioCitasContext);
  if (!ctx) {
    throw new Error('useConsultorioCitas debe usarse dentro de ConsultorioCitasProvider');
  }
  return ctx;
}

export function useConsultorioCitasOptional() {
  return useContext(ConsultorioCitasContext);
}
